import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, CalendarDays, Clock } from "lucide-react";
import { Footer } from "@/components/ui/footer";
import { useDashboardData } from "@/hooks/useDashboardData";
import { FullScreenLoader } from "@/components/ui/full-screen-loader";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { getAvatarUrl } from "@/utils/storageUtils";
import { format } from "date-fns";
import { calculateBusinessDays } from "@/utils/dateUtils";

type VacationWithProfile = {
  id: string;
  user_id: string;
  start_date: string;
  end_date: string;
  status: string;
  is_half_day: boolean;
  reason: string;
  created_at: string;
  profiles: {
    full_name: string;
    avatar_url: string;
    role: string;
  };
};

export default function AllVacations() {
  const navigate = useNavigate();
  const { isAdmin, isProfileLoading } = useDashboardData();

  const { data: vacations, isLoading } = useQuery({
    queryKey: ["all_vacations_archive"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vacation_requests")
        .select(`
          *,
          profiles (
            full_name,
            avatar_url,
            role
          )
        `)
        .in("status", ["approved", "rejected"])
        .order("start_date", { ascending: false });

      if (error) throw error;
      return data as VacationWithProfile[];
    },
  });

  if (isProfileLoading) return <FullScreenLoader />;
  if (!isAdmin) return <Navigate to="/" replace />;

  const statusColor = (status: string) => {
    switch (status) {
      case "approved": return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "rejected": return "bg-red-100 text-red-700 border-red-200";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1" style={{
        background: 'linear-gradient(109.6deg, rgba(223,234,247,1) 11.2%, rgba(244,248,252,1) 91.1%)'
      }}>
        <div className="max-w-7xl mx-auto px-8 py-10 space-y-6">
          <Button variant="ghost" onClick={() => navigate("/")} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>

          <Card className="hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <CardTitle>
                All Vacations
                <span className="block text-sm text-muted-foreground font-normal">
                  Complete history of all team vacation requests
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div>Loading...</div>
              ) : vacations && vacations.length > 0 ? (
                <div className="space-y-3">
                  {vacations.map((vacation) => {
                    const startDate = new Date(vacation.start_date);
                    const endDate = new Date(vacation.end_date);
                    const days = calculateBusinessDays(startDate, endDate);
                    const displayDays = vacation.is_half_day ? days * 0.5 : days;

                    return (
                      <div
                        key={vacation.id}
                        className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow"
                      >
                        <Avatar className="h-10 w-10 flex-shrink-0">
                          <AvatarImage src={getAvatarUrl(vacation.profiles.avatar_url)} className="object-cover" />
                          <AvatarFallback className="text-xs font-medium">
                            {vacation.profiles.full_name?.split(" ").map((n) => n[0]).join("")}
                          </AvatarFallback>
                        </Avatar>

                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm text-foreground truncate">
                            {vacation.profiles.full_name}
                          </p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {vacation.profiles.role} · {vacation.reason}
                          </p>
                        </div>

                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <CalendarDays className="h-4 w-4" />
                          <span>{format(startDate, "MMM d, yyyy")} → {format(endDate, "MMM d, yyyy")}</span>
                        </div>

                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>{displayDays}d{vacation.is_half_day ? " (½)" : ""}</span>
                        </div>

                        <Badge variant="outline" className={statusColor(vacation.status)}>
                          {vacation.status}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No vacation records found
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
